"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.notNull = exports.toSnakeCase = exports.isValidDate = exports.equals = exports.Deferred = exports.sleep = exports.sortByStreamId = void 0;
exports.compareSimple = compareSimple;
exports.compare = compare;
exports.sort = sort;
exports.computeNextBackoffInterval = computeNextBackoffInterval;
exports.withTimeout = withTimeout;
exports.orTimeout = orTimeout;
exports.retry = retry;
exports.equalsMap = equalsMap;
exports.ageSeconds = ageSeconds;
exports.awaitCondition = awaitCondition;
exports.isValidUuid = isValidUuid;
exports.isValidUuidV4 = isValidUuidV4;
exports.clone = clone;
exports.omitProperties = omitProperties;
exports.toBooleanString = toBooleanString;
exports.applianceCommandSuccessResult = applianceCommandSuccessResult;
exports.applianceCommandErrorResult = applianceCommandErrorResult;
exports.pick = pick;
exports.assertExclude = assertExclude;
exports.pairs = pairs;
exports.max = max;
exports.omitNullValuesShallow = omitNullValuesShallow;
exports.sortObjectByKeys = sortObjectByKeys;
exports.areObjectsEqual = areObjectsEqual;
const canonicalize_1 = require("./canonicalize");
const serialization_1 = require("./serialization");
function compareSimple(t1, t2) {
    if (t1 < t2) {
        return -1;
    }
    if (t1 > t2) {
        return 1;
    }
    return 0;
}
function compare(t1, t2, map) {
    const v1 = map(t1);
    const v2 = map(t2);
    for (let i = 0; i < v1.length; i++) {
        const r = compareSimple(v1[i], v2[i]);
        if (r !== 0) {
            return r;
        }
    }
    return 0;
}
function sort(...k) {
    return (t1, t2) => compare(t1, t2, (t) => k.map((keyOrSelector) => (typeof keyOrSelector !== 'function' ? t[keyOrSelector] : keyOrSelector(t))));
}
const sortByStreamId = (a, b) => {
    if (a.streamId === b.streamId)
        return 0;
    if (a.streamId > b.streamId)
        return 1;
    return -1;
};
exports.sortByStreamId = sortByStreamId;
// Credit: https://github.com/ide/backoff
// MIT License
// Copyright (c) 2019 James Ide
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
function computeNextBackoffInterval(initialBackoff, previousRetryCount, { multiplier = 1.5, randomizationFactor = 0.25, minBackoff = initialBackoff, maxBackoff = Infinity } = {}) {
    assert(initialBackoff > 0, `The initial backoff interval must be positive`);
    assert(previousRetryCount >= 0, `The previous retry count must not be negative`);
    assert(multiplier >= 1, `The backoff multiplier must be greater than or equal to 1`);
    assert(0 <= randomizationFactor && randomizationFactor <= 1, `The randomization factor must be between 0 and 1, inclusive`);
    assert(minBackoff >= 0, `The minimum backoff interval must be positive`);
    const nextBackoff = initialBackoff * multiplier ** previousRetryCount;
    // Apply jitter within the negative to positive range of the randomization factor
    const jitterFactor = 1 - randomizationFactor + 2 * randomizationFactor * Math.random();
    return Math.min(Math.max(nextBackoff * jitterFactor, minBackoff), maxBackoff);
}
function assert(cond, message) {
    if (!cond) {
        throw new Error(message);
    }
}
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
exports.sleep = sleep;
function withTimeout(timeoutMs, fn) {
    return () => {
        const timeout = (0, exports.sleep)(timeoutMs).then(() => Promise.reject(new Error('Timeout')));
        return Promise.race([fn(), timeout]);
    };
}
async function orTimeout(p, timeoutMs) {
    return await Promise.race([
        p,
        (0, exports.sleep)(timeoutMs).then(() => {
            throw new Error(`Timeout ${timeoutMs}ms`);
        }),
    ]);
}
async function retry(op, opt) {
    const { maxRetries, initialBackoff, maxBackoff } = opt;
    let lastError;
    for (let i = 0; i <= maxRetries; i++) {
        try {
            return await op();
        }
        catch (e) {
            lastError = e;
            if (i < maxRetries) {
                const nextSleep = computeNextBackoffInterval(initialBackoff, i, { maxBackoff: maxBackoff | Infinity });
                await (0, exports.sleep)(nextSleep);
                continue;
            }
        }
    }
    throw lastError;
}
function equalsMap(map) {
    return (t1, t2) => {
        return map(t1) === map(t2);
    };
}
function ageSeconds(d, now = new Date()) {
    return Math.floor((now.valueOf() - d.valueOf()) / 1000);
}
class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
exports.Deferred = Deferred;
async function awaitCondition(cond, timeoutMs, intervalMs, throwOnError = false) {
    let v = false;
    const start = Date.now();
    function maybeThrow(p) {
        return p.catch((e) => {
            if (throwOnError) {
                throw e;
            }
            return false;
        });
    }
    while (!(v = await maybeThrow(cond())) && Date.now() - start < timeoutMs) {
        await (0, exports.sleep)(intervalMs);
    }
    return v;
}
function isValidUuid(uuid, version) {
    const uuidRegex = version
        ? new RegExp(`^[0-9a-f]{8}-[0-9a-f]{4}-${version}[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$`, 'i')
        : /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
}
/** @deprecated Use isValidUuid instead. isValidUuidV4 also allows uuid v1-v5. */
function isValidUuidV4(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
}
function clone(t) {
    return (0, serialization_1.jsonParse)((0, serialization_1.jsonStringify)(t));
}
function omitProperties(t, ...k) {
    const c = clone(t);
    for (const p of k) {
        delete c[p];
    }
    return c;
}
function toBooleanString(b) {
    if (b) {
        return 'yes';
    }
    return 'no';
}
function applianceCommandSuccessResult(command, result) {
    return {
        type: 'commandResult',
        data: {
            success: true,
            result: result || {},
            command,
        },
    };
}
function applianceCommandErrorResult(command, errorMessage) {
    return {
        type: 'commandResult',
        data: {
            success: false,
            errorMessage,
            command,
        },
    };
}
const equals = (objA, objB) => {
    return (0, canonicalize_1.canonicalize)(objA) == (0, canonicalize_1.canonicalize)(objB);
};
exports.equals = equals;
function pick(t, ...pickKeys) {
    if (!t) {
        return t;
    }
    const o = {};
    for (const k of pickKeys) {
        o[k] = t[k];
    }
    return o;
}
function assertExclude(value, excluded, errorMessage) {
    if (value == excluded) {
        throw new Error(errorMessage || `Unexpected value: ${excluded}`);
    }
    return value;
}
/**
 * Return pairs with one item from each of the sequences
 *
 * @param itemsA itemsA[i] == result[i][0]
 * @param itemsB itemsB[i] == result[i][1]
 */
function pairs(itemsA, itemsB) {
    if (itemsA.length !== itemsB.length) {
        throw new Error(`Can only pair sequences of the same length (${itemsA.length} != ${itemsB.length})`);
    }
    const result = [];
    for (let i = 0; i < itemsA.length; i++) {
        result.push([itemsA[i], itemsB[i]]);
    }
    return result;
}
function max(nums) {
    let leader = Number.NEGATIVE_INFINITY;
    for (const n of nums) {
        if (n > leader) {
            leader = n;
        }
    }
    return leader;
}
const isValidDate = (date) => Object.prototype.toString.call(date) === '[object Date]' && Number.isFinite(date.getTime());
exports.isValidDate = isValidDate;
function omitNullValuesShallow(obj) {
    const returned = {};
    for (const k of Object.keys(obj)) {
        if (obj[k] !== null) {
            returned[k] = obj[k];
        }
    }
    return returned;
}
const toSnakeCase = (input) => {
    return input
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase();
};
exports.toSnakeCase = toSnakeCase;
const notNull = (v) => v !== null;
exports.notNull = notNull;
function sortObjectByKeys(unsorted) {
    return Object.keys(unsorted)
        .sort()
        .reduce((sorted, key) => {
        const value = unsorted[key];
        const isObject = !!(value && typeof value === 'object' && !Array.isArray(value));
        return { ...sorted, [key]: isObject ? sortObjectByKeys(value) : value };
    }, {});
}
function areObjectsEqual(a, b) {
    if (a && b) {
        return JSON.stringify(sortObjectByKeys(a)) === JSON.stringify(sortObjectByKeys(b));
    }
    return !a && !b;
}
//# sourceMappingURL=data:application/json;base64,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